import React from "react";
import { HairlineList } from "@/components/primitives";
import { useDocumentMeta } from "@/lib/meta";
import {
  ProductHero,
  ContentSection,
  AdjacentCrossSell,
  ProductFinalCTA,
} from "@/components/ProductSections";

export default function Training() {
  useDocumentMeta({
    fullTitle: "AML and compliance training for law firm staff | Beyond the Box",
    description:
      "Bespoke AML, process and policy, and practice-area training for law firms, with short quizzes that update each person's training record, so learning is delivered and evidenced in a few clicks.",
    path: "/training",
  });
  return (
    <div data-testid="training-page">
      <ProductHero
        testid="training-hero"
        eyebrow="Training · Competence"
        title="Training your team will finish. Records that keep themselves."
        subhead="Bespoke AML, process and policy, and practice-area training, built around how your firm actually works. Every module ends in a short quiz, and every result lands on that person's training record. Learning delivered and evidenced at the touch of a few buttons."
        primaryLabel="Book a walkthrough"
        secondaryLabel="Pair it with ComplianceMinder"
        secondaryTo="#adjacent"
      />

      <ContentSection
        n="01"
        eyebrow="What we build"
        title="Your firm's training, not a generic library"
        testid="training-what"
      >
        <p className="text-lg leading-relaxed text-foreground">
          Off-the-shelf courses teach somebody else's procedures. We design training around your
          policies, your processes and the work your people actually do, so what they learn is what
          you need them to do on Monday morning.
        </p>
        <HairlineList
          testid="training-modules"
          items={[
            "AML: your firm-wide risk assessment, your CDD and source-of-funds steps, your reporting route to the MLRO",
            "Process and policy: how your firm opens matters, handles client money, manages complaints and keeps data safe",
            "Practice areas: matter-specific modules for the teams who need them, from conveyancing to private client",
            "Updated when your policies change, so training never lags behind the manual",
          ]}
        />
      </ContentSection>

      <ContentSection
        n="02"
        eyebrow="Quizzes and records"
        title="Every module checked. Every result recorded."
        testid="training-records"
      >
        <p className="text-lg leading-relaxed text-foreground">
          A short quiz at the end of each module confirms the learning has landed. Pass it, and the
          result is written straight to that person's training record, with the date, the module
          and the score.
        </p>
        <p className="mt-5 leading-relaxed text-muted-foreground">
          Owners, the COLP and the MLRO can see who has completed what, who is overdue, and where a
          team needs a refresher. No chasing, no spreadsheet of certificates.
        </p>
        <HairlineList
          testid="training-records-list"
          items={[
            "Short, focused quizzes rather than hour-long videos",
            "Results recorded automatically against each person",
            "Clear view of completions, gaps and overdue training",
            "Evidence ready when the regulator or your insurer asks",
          ]}
        />
      </ContentSection>

      <ContentSection
        n="03"
        eyebrow="Why it matters"
        title="Training you can prove, not just training you did"
        testid="training-why"
      >
        <p className="text-lg leading-relaxed text-foreground">
          Firms must make sure relevant staff are trained in anti-money laundering and keep a
          written record of that training. The SRA also expects firms to keep their people
          competent, and expects solicitors to reflect on and address their own learning needs.
        </p>
        <p className="mt-5 leading-relaxed text-muted-foreground">
          The hard part is rarely the training itself. It is showing, on the day someone asks, that
          it happened, that it was relevant, and that it stuck. That is what the records are for.
        </p>
      </ContentSection>

      <AdjacentCrossSell
        testid="training-adjacent"
        title="Connect training to live compliance"
        body="ComplianceMinder keeps your policies live and your risk visible. Training built on the same policies means that when a policy changes, the people who need to know can be retrained, and the record shows it."
        to="/complianceminder"
        label="Explore ComplianceMinder"
      />

      <ProductFinalCTA
        testid="training-final-cta"
        title="Competence your firm can evidence."
        body="Tell us what your team needs to know. We'll build it, test it and record it."
        label="Book a walkthrough"
      />
    </div>
  );
}
